import { useState } from 'react';
import type { UnitKey } from '@fewerbags/schema';
import {
  applyQuantityText,
  emptyIngredientRow,
  moveItem,
  rowsFromPastedText,
  type IngredientRow,
} from '../../pages/recipes/recipe-form';
import { IngredientNameInput } from './IngredientNameInput';
import { RowControls } from './RowControls';
import { UNIT_OPTIONS } from '../../lib/units';

export function IngredientRowsEditor({
  rows,
  onChange,
}: {
  rows: IngredientRow[];
  onChange: (rows: IngredientRow[]) => void;
}) {
  const [paste, setPaste] = useState('');
  const [pasteOpen, setPasteOpen] = useState(false);

  function patch(index: number, next: Partial<IngredientRow>) {
    onChange(rows.map((row, i) => (i === index ? { ...row, ...next } : row)));
  }

  function applyPaste() {
    const parsed = rowsFromPastedText(paste);
    if (parsed.length === 0) return;
    const keep = rows.filter((r) => r.name.trim() !== '' || r.ingredientId !== null);
    onChange([...keep, ...parsed]);
    setPaste('');
    setPasteOpen(false);
  }

  return (
    <div className="flex flex-col gap-3">
      <ul className="flex flex-col gap-3">
        {rows.map((row, i) => (
          <li key={row.key} className="rounded-lg border border-line p-3">
            <div className="flex flex-wrap items-end gap-2">
              <div className="flex w-24 flex-col gap-1">
                <label htmlFor={`qty-${row.key}`} className="text-sm font-medium text-ink">
                  Amount
                </label>
                <input
                  id={`qty-${row.key}`}
                  value={row.qtyText}
                  inputMode="text"
                  placeholder="1 1/2"
                  onChange={(e) => patch(i, { qtyText: e.target.value })}
                  onBlur={() => onChange(rows.map((r, j) => (j === i ? applyQuantityText(r) : r)))}
                  className="min-h-11 rounded-lg border border-line bg-surface px-3 text-base text-ink placeholder:text-muted"
                />
              </div>
              <div className="flex w-32 flex-col gap-1">
                <label htmlFor={`unit-${row.key}`} className="text-sm font-medium text-ink">
                  Unit
                </label>
                <select
                  id={`unit-${row.key}`}
                  value={row.unit}
                  onChange={(e) => patch(i, { unit: e.target.value as UnitKey })}
                  className="min-h-11 rounded-lg border border-line bg-surface px-2 text-base text-ink"
                >
                  {UNIT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <IngredientNameInput
                label={`Ingredient ${i + 1}`}
                hideLabel={i > 0}
                value={row.name}
                onChange={(name, ingredientId) => patch(i, { name, ingredientId })}
              />
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <div className="flex min-w-40 flex-1 flex-col gap-1">
                <label htmlFor={`prep-${row.key}`} className="sr-only">
                  Preparation note for ingredient {i + 1}
                </label>
                <input
                  id={`prep-${row.key}`}
                  value={row.prepNote}
                  placeholder="prep note — diced, drained…"
                  onChange={(e) => patch(i, { prepNote: e.target.value })}
                  className="min-h-11 rounded-lg border border-line bg-surface px-3 text-base text-ink placeholder:text-muted"
                />
              </div>
              <label className="flex min-h-11 items-center gap-2 text-sm text-ink">
                <input
                  type="checkbox"
                  checked={row.optional}
                  onChange={(e) => patch(i, { optional: e.target.checked })}
                  className="size-5 accent-[var(--color-brand)]"
                />
                Optional
              </label>
              <RowControls
                label={`ingredient ${i + 1}`}
                index={i}
                total={rows.length}
                onMove={(to) => onChange(moveItem(rows, i, to))}
                onRemove={() => onChange(rows.filter((_, j) => j !== i))}
              />
            </div>
          </li>
        ))}
      </ul>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => onChange([...rows, emptyIngredientRow()])}
          className="min-h-11 rounded-lg border border-line px-3 text-sm font-semibold text-brand"
        >
          Add ingredient
        </button>
        <button
          type="button"
          onClick={() => setPasteOpen((v) => !v)}
          aria-expanded={pasteOpen}
          className="min-h-11 rounded-lg border border-line px-3 text-sm font-semibold text-brand"
        >
          Paste ingredients
        </button>
      </div>

      {pasteOpen ? (
        <div className="flex flex-col gap-2 rounded-lg border border-line bg-cream p-3">
          <label htmlFor="paste-ingredients" className="text-sm font-medium text-ink">
            One ingredient per line
          </label>
          <textarea
            id="paste-ingredients"
            rows={6}
            value={paste}
            onChange={(e) => setPaste(e.target.value)}
            placeholder={'1 1/2 cups flour\n2 cloves garlic, minced\nsalt (optional)'}
            className="rounded-lg border border-line bg-surface p-3 text-base text-ink placeholder:text-muted"
          />
          <p className="text-xs text-muted">
            Each line is split into amount, unit, ingredient and prep note. Check the rows
            afterwards — the parser is good, not perfect.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={applyPaste}
              disabled={paste.trim() === ''}
              className="min-h-11 rounded-lg bg-brand px-3 text-sm font-semibold text-white disabled:opacity-50"
            >
              Add these
            </button>
            <button
              type="button"
              onClick={() => {
                setPaste('');
                setPasteOpen(false);
              }}
              className="min-h-11 rounded-lg border border-line px-3 text-sm font-semibold text-ink"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
